import { useState } from 'react';
import { Alert, AlertFormData, SelectedBrand, SelectedSize, SelectedMaterial, SelectedPattern, SelectedLanguage, SelectedPlatform, SelectedRating } from '../types/alert.types';
import { getColorByTitle } from '../utils/vintedColors';

/**
 * Ajoute le paramètre catalog à l'URL Vinted
 */
const addCatalogToUrl = (url: string, categoryId: string): string => {
  try {
    const urlObj = new URL(url);

    // Supprimer les anciens paramètres catalog[] et catalog s'ils existent
    const params = new URLSearchParams(urlObj.search);
    const paramsArray: [string, string][] = [];

    params.forEach((value, key) => {
      if (key !== 'catalog[]' && key !== 'catalog') {
        paramsArray.push([key, value]);
      }
    });

    // Ajouter le nouveau paramètre catalog (sans [])
    paramsArray.push(['catalog', categoryId]);

    // Reconstruire l'URL
    urlObj.search = new URLSearchParams(paramsArray).toString();
    return urlObj.toString();
  } catch (e) {
    // Si l'URL est invalide, la retourner telle quelle
    console.error('URL invalide:', e);
    return url;
  }
};

/**
 * Ajoute les paramètres brand_ids à l'URL Vinted
 */
const updateBrandsInUrl = (url: string, brands: SelectedBrand[]): string => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    // Supprimer tous les anciens paramètres brand_ids et brand_ids[]
    const paramsArray: [string, string][] = [];
    params.forEach((value, key) => {
      if (key !== 'brand_ids[]' && key !== 'brand_ids') {
        paramsArray.push([key, value]);
      }
    });

    // Ajouter les nouveaux paramètres brand_ids (sans [])
    brands.forEach(brand => {
      paramsArray.push(['brand_ids', brand.id.toString()]);
    });

    urlObj.search = new URLSearchParams(paramsArray).toString();
    return urlObj.toString();
  } catch (e) {
    console.error('URL invalide:', e);
    return url;
  }
};

/**
 * Ajoute les paramètres status_ids à l'URL Vinted
 */
const updateStatusesInUrl = (url: string, statuses: number[]): string => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    // Supprimer tous les anciens paramètres status_ids[] et status_ids
    const paramsArray: [string, string][] = [];
    params.forEach((value, key) => {
      if (key !== 'status_ids[]' && key !== 'status_ids') {
        paramsArray.push([key, value]);
      }
    });

    // Ajouter les nouveaux paramètres status_ids (sans [])
    statuses.forEach(statusId => {
      paramsArray.push(['status_ids', statusId.toString()]);
    });

    urlObj.search = new URLSearchParams(paramsArray).toString();
    return urlObj.toString();
  } catch (e) {
    console.error('URL invalide:', e);
    return url;
  }
};

/**
 * Ajoute les paramètres color_ids à l'URL Vinted
 */
const updateColorsInUrl = (url: string, colors: string[]): string => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    // Supprimer tous les anciens paramètres color_ids[] et color_ids
    const paramsArray: [string, string][] = [];
    params.forEach((value, key) => {
      if (key !== 'color_ids[]' && key !== 'color_ids') {
        paramsArray.push([key, value]);
      }
    });

    // Convertir les titres de couleurs en IDs et ajouter les nouveaux paramètres color_ids (sans [])
    colors.forEach(colorTitle => {
      const colorObj = getColorByTitle(colorTitle);
      if (colorObj) {
        paramsArray.push(['color_ids', colorObj.id.toString()]);
      }
    });

    urlObj.search = new URLSearchParams(paramsArray).toString();
    return urlObj.toString();
  } catch (e) {
    console.error('URL invalide:', e);
    return url;
  }
};

/**
 * Ajoute les paramètres size_ids à l'URL Vinted
 */
const updateSizesInUrl = (url: string, sizes: SelectedSize[]): string => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    // Supprimer tous les anciens paramètres size_ids[] et size_ids
    const paramsArray: [string, string][] = [];
    params.forEach((value, key) => {
      if (key !== 'size_ids[]' && key !== 'size_ids') {
        paramsArray.push([key, value]);
      }
    });

    // Ajouter les nouveaux paramètres size_ids (sans [])
    sizes.forEach(size => {
      if (size.id > 0) {  // Ignorer les tailles sans ID valide (ex: jeux vidéo)
        paramsArray.push(['size_ids', size.id.toString()]);
      }
    });

    urlObj.search = new URLSearchParams(paramsArray).toString();
    return urlObj.toString();
  } catch (e) {
    console.error('URL invalide:', e);
    return url;
  }
};

/**
 * Ajoute les paramètres material_ids à l'URL Vinted
 */
const updateMaterialsInUrl = (url: string, materials: SelectedMaterial[]): string => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    // Supprimer tous les anciens paramètres material_ids
    const paramsArray: [string, string][] = [];
    params.forEach((value, key) => {
      if (key !== 'material_ids[]' && key !== 'material_ids') {
        paramsArray.push([key, value]);
      }
    });

    // Ajouter les nouveaux paramètres material_ids
    materials.forEach(material => {
      paramsArray.push(['material_ids', material.id.toString()]);
    });

    urlObj.search = new URLSearchParams(paramsArray).toString();
    return urlObj.toString();
  } catch (e) {
    console.error('URL invalide:', e);
    return url;
  }
};

/**
 * Ajoute les paramètres pattern_ids à l'URL Vinted (format officiel Vinted)
 */
const updatePatternsInUrl = (url: string, patterns: SelectedPattern[]): string => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    // Supprimer tous les anciens paramètres pattern_ids / patterns_ids
    const paramsArray: [string, string][] = [];
    params.forEach((value, key) => {
      if (key !== 'pattern_ids[]' && key !== 'pattern_ids' && key !== 'patterns_ids[]' && key !== 'patterns_ids') {
        paramsArray.push([key, value]);
      }
    });

    // Ajouter les nouveaux paramètres pattern_ids (format Vinted)
    patterns.forEach(pattern => {
      paramsArray.push(['pattern_ids', pattern.id.toString()]);
    });

    urlObj.search = new URLSearchParams(paramsArray).toString();
    return urlObj.toString();
  } catch (e) {
    console.error('URL invalide:', e);
    return url;
  }
};

/**
 * Ajoute les paramètres video_game_platform_ids à l'URL Vinted
 */
const updatePlatformsInUrl = (url: string, platforms: SelectedPlatform[]): string => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    // Supprimer tous les anciens paramètres video_game_platform_ids
    const paramsArray: [string, string][] = [];
    params.forEach((value, key) => {
      if (key !== 'video_game_platform_ids[]' && key !== 'video_game_platform_ids') {
        paramsArray.push([key, value]);
      }
    });

    // Ajouter les nouveaux paramètres video_game_platform_ids
    platforms.forEach(platform => {
      paramsArray.push(['video_game_platform_ids', platform.id.toString()]);
    });

    urlObj.search = new URLSearchParams(paramsArray).toString();
    return urlObj.toString();
  } catch (e) {
    console.error('URL invalide:', e);
    return url;
  }
};

/**
 * Ajoute les paramètres video_game_rating_ids à l'URL Vinted
 */
const updateRatingsInUrl = (url: string, ratings: SelectedRating[]): string => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    // Supprimer tous les anciens paramètres video_game_rating_ids
    const paramsArray: [string, string][] = [];
    params.forEach((value, key) => {
      if (key !== 'video_game_rating_ids[]' && key !== 'video_game_rating_ids') {
        paramsArray.push([key, value]);
      }
    });

    // Ajouter les nouveaux paramètres video_game_rating_ids
    ratings.forEach(rating => {
      paramsArray.push(['video_game_rating_ids', rating.id.toString()]);
    });

    urlObj.search = new URLSearchParams(paramsArray).toString();
    return urlObj.toString();
  } catch (e) {
    console.error('URL invalide:', e);
    return url;
  }
};

/**
 * Ajoute les paramètres language_book_ids à l'URL Vinted
 */
const updateLanguagesInUrl = (url: string, languages: SelectedLanguage[]): string => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    // Supprimer tous les anciens paramètres language_book_ids
    const paramsArray: [string, string][] = [];
    params.forEach((value, key) => {
      if (key !== 'language_book_ids[]' && key !== 'language_book_ids') {
        paramsArray.push([key, value]);
      }
    });

    // Ajouter les nouveaux paramètres language_book_ids
    languages.forEach(language => {
      paramsArray.push(['language_book_ids', language.id]);
    });

    urlObj.search = new URLSearchParams(paramsArray).toString();
    return urlObj.toString();
  } catch (e) {
    console.error('URL invalide:', e);
    return url;
  }
};

/**
 * Ajoute les paramètres price_from et price_to à l'URL Vinted
 */
const updatePriceInUrl = (url: string, minPrice: string, maxPrice: string): string => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    // Supprimer les anciens paramètres price_from et price_to
    const paramsArray: [string, string][] = [];
    params.forEach((value, key) => {
      if (key !== 'price_from' && key !== 'price_to') {
        paramsArray.push([key, value]);
      }
    });

    // Ajouter les nouveaux paramètres price_from et price_to
    if (minPrice && parseFloat(minPrice) > 0) {
      paramsArray.push(['price_from', minPrice]);
    }
    if (maxPrice && parseFloat(maxPrice) > 0) {
      paramsArray.push(['price_to', maxPrice]);
    }

    urlObj.search = new URLSearchParams(paramsArray).toString();
    return urlObj.toString();
  } catch (e) {
    console.error('URL invalide:', e);
    return url;
  }
};

/**
 * Supprime le paramètre catalog de l'URL Vinted
 */
const removeCatalogFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    // Créer un nouveau URLSearchParams sans les catalog[] et catalog
    const paramsArray: [string, string][] = [];
    params.forEach((value, key) => {
      if (key !== 'catalog[]' && key !== 'catalog') {
        paramsArray.push([key, value]);
      }
    });

    urlObj.search = new URLSearchParams(paramsArray).toString();
    return urlObj.toString();
  } catch (e) {
    console.error('URL invalide:', e);
    return url;
  }
};

const getInitialFormData = (editingAlert?: Alert): AlertFormData => {
  if (editingAlert) {
    // Gérer la migration des anciennes données (strings) vers les nouvelles (objets {id, title})
    const brands = editingAlert.selected_brands || [];
    const selectedBrands: SelectedBrand[] = brands.map((brand: any) => {
      // Si c'est déjà un objet avec id et title, le garder tel quel
      if (typeof brand === 'object' && brand.id && brand.title) {
        return brand as SelectedBrand;
      }
      // Si c'est une string (anciennes données), créer un objet temporaire avec id = 0
      return { id: 0, title: brand as string };
    });

    // Gérer la migration des tailles (strings) vers les nouvelles (objets {id, title})
    const sizes = editingAlert.selected_sizes || [];
    const selectedSizes: SelectedSize[] = sizes.map((size: any) => {
      // Si c'est déjà un objet avec id et title, le garder tel quel
      if (typeof size === 'object' && size.id !== undefined && size.title) {
        return size as SelectedSize;
      }
      // Si c'est une string (anciennes données), créer un objet temporaire avec id = 0
      return { id: 0, title: size as string };
    });

    // Gérer la migration des matières (IDs) vers les nouvelles (objets {id, title})
    const materials = editingAlert.selected_materials || [];
    const selectedMaterials: SelectedMaterial[] = materials.map((material: any) => {
      if (typeof material === 'object' && material.id !== undefined && material.title) {
        return material as SelectedMaterial;
      }
      return { id: material as number, title: `Matière ${material}` };
    });

    // Gérer la migration des motifs (IDs) vers les nouvelles (objets {id, title})
    const patterns = editingAlert.selected_patterns || [];
    const selectedPatterns: SelectedPattern[] = patterns.map((pattern: any) => {
      if (typeof pattern === 'object' && pattern.id !== undefined && pattern.title) {
        return pattern as SelectedPattern;
      }
      return { id: pattern as number, title: `Motif ${pattern}` };
    });

    // Gérer la migration des langues (IDs) vers les nouvelles (objets {id, title})
    const languages = editingAlert.selected_languages || [];
    const selectedLanguages: SelectedLanguage[] = languages.map((language: any) => {
      if (typeof language === 'object' && language.id && language.title) {
        return language as SelectedLanguage;
      }
      return { id: language as string, title: `Langue ${language}` };
    });

    // Gérer la migration des plateformes (IDs) vers les nouvelles (objets {id, title})
    const platforms = editingAlert.selected_platforms || [];
    const selectedPlatforms: SelectedPlatform[] = platforms.map((platform: any) => {
      if (typeof platform === 'object' && platform.id !== undefined && platform.title) {
        return platform as SelectedPlatform;
      }
      return { id: platform as number, title: `Plateforme ${platform}` };
    });

    // Gérer la migration des ratings (IDs) vers les nouvelles (objets {id, title})
    const ratings = editingAlert.selected_ratings || [];
    const selectedRatings: SelectedRating[] = ratings.map((rating: any) => {
      if (typeof rating === 'object' && rating.id !== undefined && rating.title) {
        return rating as SelectedRating;
      }
      return { id: rating as number, title: `Rating ${rating}` };
    });

    return {
      name: editingAlert.name,
      vintedUrl: editingAlert.vinted_url || '',
      searchText: '',
      categoryId: editingAlert.category_id || '',
      categoryPath: editingAlert.category_path || '',
      selectedConditions: editingAlert.selected_conditions || [],
      selectedSizes: selectedSizes,
      selectedColors: editingAlert.selected_colors || [],
      selectedBrands: selectedBrands,
      selectedStatuses: editingAlert.selected_statuses || [],
      selectedLanguages: selectedLanguages,
      selectedMaterials: selectedMaterials,
      selectedPatterns: selectedPatterns,
      selectedPlatforms: selectedPlatforms,
      selectedRatings: selectedRatings,
      minPrice: editingAlert.min_price?.toString() || '',
      maxPrice: editingAlert.max_price?.toString() || '',
      minLikes: editingAlert.min_likes?.toString() || '',
      maxLikes: editingAlert.max_likes?.toString() || '',
      maxPages: editingAlert.max_pages.toString(),
      trendingOnly: editingAlert.trending_only || false,
      mustInclude: editingAlert.must_include || [],
      mustExclude: editingAlert.must_exclude || []
    };
  }

  return {
    name: '',
    vintedUrl: '',
    searchText: '',
    categoryId: '',
    categoryPath: '',
    selectedConditions: [],
    selectedSizes: [],
    selectedColors: [],
    selectedBrands: [],
    selectedStatuses: [],
    selectedLanguages: [],
    selectedMaterials: [],
    selectedPatterns: [],
    selectedPlatforms: [],
    selectedRatings: [],
    minPrice: '',
    maxPrice: '',
    minLikes: '',
    maxLikes: '',
    maxPages: '5',
    trendingOnly: false,
    mustInclude: [],
    mustExclude: []
  };
};

export const useAlertForm = (editingAlert?: Alert) => {
  const [formData, setFormData] = useState<AlertFormData>(() => getInitialFormData(editingAlert));
  const [currentStep, setCurrentStep] = useState(1);

  const updateField = <K extends keyof AlertFormData>(field: K, value: AlertFormData[K]) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      // Si les marques changent, mettre à jour l'URL avec brand_ids[]
      if (field === 'selectedBrands' && newData.vintedUrl) {
        newData.vintedUrl = updateBrandsInUrl(newData.vintedUrl, value as SelectedBrand[]);
      }

      // Si les états changent, mettre à jour l'URL avec status_ids[]
      if (field === 'selectedStatuses' && newData.vintedUrl) {
        newData.vintedUrl = updateStatusesInUrl(newData.vintedUrl, value as number[]);
      }

      // Si les couleurs changent, mettre à jour l'URL avec color_ids[]
      if (field === 'selectedColors' && newData.vintedUrl) {
        newData.vintedUrl = updateColorsInUrl(newData.vintedUrl, value as string[]);
      }

      // Si les matières changent, mettre à jour l'URL avec material_ids[]
      if (field === 'selectedMaterials' && newData.vintedUrl) {
        newData.vintedUrl = updateMaterialsInUrl(newData.vintedUrl, value as SelectedMaterial[]);
      }

      // Si les motifs changent, mettre à jour l'URL avec patterns_ids[]
      if (field === 'selectedPatterns' && newData.vintedUrl) {
        newData.vintedUrl = updatePatternsInUrl(newData.vintedUrl, value as SelectedPattern[]);
      }

      // Si les plateformes changent, mettre à jour l'URL avec video_game_platform_ids[]
      if (field === 'selectedPlatforms' && newData.vintedUrl) {
        newData.vintedUrl = updatePlatformsInUrl(newData.vintedUrl, value as SelectedPlatform[]);
      }

      // Si les classifications changent, mettre à jour l'URL avec video_game_rating_ids[]
      if (field === 'selectedRatings' && newData.vintedUrl) {
        newData.vintedUrl = updateRatingsInUrl(newData.vintedUrl, value as SelectedRating[]);
      }

      // Si les langues changent, mettre à jour l'URL avec language_book_ids[]
      if (field === 'selectedLanguages' && newData.vintedUrl) {
        newData.vintedUrl = updateLanguagesInUrl(newData.vintedUrl, value as SelectedLanguage[]);
      }

      // Si les tailles changent, mettre à jour l'URL avec size_ids[]
      if (field === 'selectedSizes' && newData.vintedUrl) {
        newData.vintedUrl = updateSizesInUrl(newData.vintedUrl, value as SelectedSize[]);
      }

      // Si le prix minimum change, mettre à jour l'URL avec price_from
      if (field === 'minPrice' && newData.vintedUrl) {
        newData.vintedUrl = updatePriceInUrl(newData.vintedUrl, value as string, newData.maxPrice);
      }

      // Si le prix maximum change, mettre à jour l'URL avec price_to
      if (field === 'maxPrice' && newData.vintedUrl) {
        newData.vintedUrl = updatePriceInUrl(newData.vintedUrl, newData.minPrice, value as string);
      }

      return newData;
    });
  };

  const updateMultipleFields = (updates: Partial<AlertFormData>) => {
    setFormData(prev => {
      const newData = { ...prev, ...updates };

      // Si une catégorie est sélectionnée, ajouter &catalog[]=<categoryId> à l'URL
      if (updates.categoryId !== undefined) {
        if (updates.categoryId && newData.vintedUrl) {
          // Ajouter le paramètre catalog[] à l'URL
          newData.vintedUrl = addCatalogToUrl(newData.vintedUrl, updates.categoryId);
        } else if (!updates.categoryId && newData.vintedUrl) {
          // Si la catégorie est retirée, supprimer le paramètre catalog[] de l'URL
          newData.vintedUrl = removeCatalogFromUrl(newData.vintedUrl);
        }
      }

      return newData;
    });
  };

  const resetForm = () => {
    setFormData(getInitialFormData());
    setCurrentStep(1);
  };

  const canGoNext = (): boolean => {
    switch (currentStep) {
      case 1:
        return formData.name.trim() !== '';
      case 2:
        return formData.vintedUrl.trim() !== '' || (formData.searchText || '').trim() !== '';
      case 3:
      case 4:
      case 5:
        return true; // Ces étapes sont optionnelles
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step: number) => {
    if (step >= 1 && step <= 5) {
      setCurrentStep(step);
    }
  };

  return {
    formData,
    currentStep,
    updateField,
    updateMultipleFields,
    resetForm,
    canGoNext,
    nextStep,
    prevStep,
    goToStep,
    setCurrentStep
  };
};

/**
 * Construit l'URL finale pour la sauvegarde (recherche rapide ou URL collée + filtres)
 */
export const buildFinalVintedUrl = (formData: AlertFormData): string => {
  let url: string;
  const searchText = (formData.searchText || '').trim();
  const vintedUrl = (formData.vintedUrl || '').trim();

  if (vintedUrl) {
    url = vintedUrl;
  } else if (searchText) {
    const encodedText = encodeURIComponent(searchText);
    const searchId = Math.floor(Math.random() * 100000000000);
    url = `https://www.vinted.fr/catalog?search_text=${encodedText}&search_id=${searchId}`;
  } else {
    return '';
  }

  if (formData.categoryId) {
    url = addCatalogToUrl(url, formData.categoryId);
  }
  url = updateBrandsInUrl(url, formData.selectedBrands || []);
  url = updateStatusesInUrl(url, formData.selectedStatuses || []);
  url = updateColorsInUrl(url, formData.selectedColors || []);
  url = updateSizesInUrl(url, formData.selectedSizes || []);
  url = updateMaterialsInUrl(url, formData.selectedMaterials || []);
  url = updatePatternsInUrl(url, formData.selectedPatterns || []);
  url = updatePlatformsInUrl(url, formData.selectedPlatforms || []);
  url = updateRatingsInUrl(url, formData.selectedRatings || []);
  url = updateLanguagesInUrl(url, formData.selectedLanguages || []);
  url = updatePriceInUrl(url, formData.minPrice || '', formData.maxPrice || '');

  return url;
};
